<script setup name="TvbPlayer">
import { ref, reactive, inject, watch, computed, onUnmounted } from 'vue'
// import { action } from '@/utils/gtm'
import TvbYouTuBePlayer from './components/YouTuBe/index.vue'
import TvbShakaPlayer from './components/Shaka/index.vue'
import TvbPlayIcon from './../TvbPlayIcon/index.vue'
import TvbDragBox from './../TvbDragBox/index.vue'
import TvbRecommendVideos from './components/RecommendVideos/index.vue'
import { isSafariBelow17 } from '@/utils/index'

const tvbPlayerRef = ref(null)
const emits = defineEmits([
  'stateChange',
  'play-click',
  'prev',
  'next',
  'recommend-navigate',
  'ad-start',
  'ad-end',
  'ad-error',
  'content-resume',
  'content-ended'
])
const size = inject('size')

const props = defineProps({
  src: {
    type: String,
    default: ''
  },
  posterUrl: {
    type: String,
    default: ''
  },
  autoplay: {
    type: Boolean,
    default: false
  },
  dragBox: {
    type: Boolean,
    default: false
  },
  dragBoxDisabled: {
    type: Boolean,
    default: false
  },
  mute: {
    type: Boolean,
    default: true
  },
  title: {
    type: String,
    default: ''
  },
  type: {
    type: String,
    default: 'youtube'
  },
  contentId: {
    type: String,
    default: ''
  },
  other: {
    type: Object,
    default: () => {
      return {}
    }
  },
  youboraOptions: {
    type: Object,
    default: () => ({})
  },
  regionOnly: {
    type: Number,
    default: 0
  },
  /**
   * 控制上一集/下一集按钮的显示与隐藏
   * @type {boolean}
   * @default false - 默认不显示按钮
   * @description 通过此参数可以灵活控制按钮的显示状态，适用于不同页面的需求
   */
  showEpisodeNavigation: {
    type: Boolean,
    default: false
  },
  /**
   * 分辨率配置，用于控制分辨率选择功能的显示和切换
   * @type {Object}
   * @default null - 默认不显示分辨率选择
   * @description 格式为 { sd: string, hd: string }，sd和hd分别对应480p和720p的视频URL
   */
  videoClarity: {
    type: Object,
    default: null
  },
  /**
   * 字幕配置，用于控制字幕选择功能的显示和切换
   * @type {Object}
   * @default null - 默认不显示字幕选择
   * @description 格式为 { tc: string, sc: string }，tc和sc分别对应繁體中文和简体中文字幕文件的URL
   */
  subtitle: {
    type: Object,
    default: null
  },
  /**
   * 推荐视频列表，用于视频播放结束时展示
   * @type {Array<{cover: string, title: string, url: string}>}
   * @default [] - 默认空数组，不显示推荐区域
   * @description 每个推荐项包含视频封面、标题和页面地址
   */
  featuredVideos: {
    type: Array,
    default: () => []
  },
  /**
   * Instream 广告标签 URL (VAST/VMAP)
   */
  adTagUrl: {
    type: String,
    default: ''
  },
  /**
   * 是否显示 Instream 视频广告
   */
  showInstreamAds: {
    type: Boolean,
    default: false
  }
})

// tracking
const isSendStart = ref(false)
const isTracking = computed(() => props.other?.isTracking)
const trackInfo = computed(() => props.other?.trackInfo || {})
watch(isSendStart, (value) => {
  if (value && isTracking.value) {
    console.log(trackInfo)
    // action({
    //   eventID: 'B013',
    //   eventLabel: 'album_popup',
    //   eventCategoy: 'sub_category',
    //   screenLabel: props.src || '',
    //   listName: 'video',
    //   ...trackInfo.value
    // })
  }
})

const player = reactive({ state: null })
const stateChangeTimeout = ref(null)

// 是否正在播放广告
const isAdPlaying = ref(false)

// 是否显示推荐视频区域
const showRecommendations = ref(false)

const onStateChange = ({ state, ...other }) => {
  console.log({ state, other })
  // 立即更新状态，不使用防抖（避免延迟导致UI不同步）
  // 过滤掉 'waiting' 和 'playing' 事件，避免与 'play'/'pause' 冲突
  if (state === 'waiting' || state === 'playing') {
    return
  }

  player.state = state
  emits('stateChange', { state, ...other })

  if (state === 'play') {
    isSendStart.value = true
    showRecommendations.value = false
  }
  if (state === 'ended' || state === 'pause') {
    isSendStart.value = false
  }
  // 视频结束时显示推荐区域
  if (state === 'ended' && props.featuredVideos.length > 0) {
    showRecommendations.value = true
  }
}
const isError = computed(() => props.regionOnly === 1 || player.state === 'error')
const play = () => tvbPlayerRef?.value?.play?.()
const pause = () => {
  tvbPlayerRef?.value?.pause?.()
  isSendStart.value = false
}

// 切换到上一集视频 - 桥接 Shaka Player 的 prev 事件
const onPrev = () => {
  emits('prev')
}

// 切换到下一集视频 - 桥接 Shaka Player 的 next 事件
const onNext = () => {
  emits('next')
}

// 广告开始播放
const onAdStart = (event) => {
  isAdPlaying.value = true
  emits('ad-start', event)
}

// 广告播放结束
const onAdEnd = (event) => {
  isAdPlaying.value = false
  emits('ad-end', event)
}

// 处理重播事件
const onReplay = () => {
  showRecommendations.value = false
  play()
}

// 处理推荐视频导航
const onRecommendNavigate = (video) => {
  showRecommendations.value = false
  emits('recommend-navigate', video)
}

// 处理倒计时结束自动播放下一集
const onCountdownNext = () => {
  showRecommendations.value = false
  emits('next')
}

// 检测是否为移动端
const isMobile = computed(() => {
  return ['s', 'xs'].includes(size.value)
})

onUnmounted(() => {
  if (stateChangeTimeout.value) {
    clearTimeout(stateChangeTimeout.value)
  }
})

defineExpose({
  play,
  pause,
  onPrev,
  onNext,
  showRecommendations,
  startAds: () => tvbPlayerRef.value?.startAds?.(),
  // 桥接：请求新广告（pr 结束后请求 mr，SDK 自动在 mid-roll 时间点播放）
  requestAds: (adTagUrl) => tvbPlayerRef.value?.requestAds?.(adTagUrl)
})
</script>

<template>
  <div class="tvb-player" :class="{ error: isError, [size]: size }">
    <div v-if="isError" class="tvb-player-error">
      <tvb-svg-icon class="tvb-player-error-icon" name="tips" />
      <p class="tvb-player-error-text">
        {{
          regionOnly === 1
            ? '由於版權限制, 您所在的地區無法觀看此影片'
            : '此影片播放出錯，請稍後再試'
        }}
      </p>
    </div>
    <!-- 播放按钮层：仅Shaka播放器显示，Safari 17 以下版本使用原生控件，不显示自定义播放按钮 -->
    <div
      class="tvb-player-play-layer"
      v-else-if="
        props.type === 'shaka' &&
        player.state !== 'play' &&
        !isSendStart &&
        !isSafariBelow17() &&
        !isAdPlaying
      "
    >
      <TvbPlayIcon
        :size="{ xl: 48, l: 48, m: 48, s: 40, xs: 40 }"
        class="tvb-player-play"
        @click="
          () => {
            if (player.state === 'play' || player.state === 'loading' || player.state === 'error') {
              pause()
            } else {
              // 移动端首次播放时启动广告（IMA SDK 要求用户交互调用）
              if (props.showInstreamAds && props.adTagUrl) {
                tvbPlayerRef?.startAds?.()
              }
              play()
              emits('play-click')
            }
          }
        "
        :class="{
          hide: player.state === 'play' || player.state === 'loading' || player.state === 'error'
        }"
      />
      <n-spin v-if="player.state === 'loading'" class="tvb-player-play" stroke="#EEE" />
    </div>
    <!-- 小窗（迷你播放窗）初始尺寸：宽 303px，高按 16:9 换算 170px（对齐 TC-1481 #14 设计稿） -->
    <component
      v-if="player.state !== 'error'"
      :is="props.dragBox ? TvbDragBox : 'div'"
      :disabled="player.state !== 'play' || dragBoxDisabled"
      :fixed-width="303"
      :fixed-height="170"
      class="tvb-player-container"
    >
      <TvbYouTuBePlayer
        v-bind="{ ...props, ...$attrs }"
        :mute="props.mute"
        ref="tvbPlayerRef"
        @state-change="onStateChange"
        v-if="props.type === 'youtube'"
      />
      <TvbShakaPlayer
        v-bind="{ ...props, ...$attrs }"
        :title="title"
        :youbora-options="props.youboraOptions"
        :content-id="props.contentId"
        :mute="props.mute"
        :show-episode-navigation="props.showEpisodeNavigation"
        :video-clarity="props.videoClarity"
        :subtitle="props.subtitle"
        :ad-tag-url="props.adTagUrl"
        :show-instream-ads="props.showInstreamAds"
        @state-change="onStateChange"
        @prev="onPrev"
        @next="onNext"
        @ad-start="onAdStart($event)"
        @ad-end="onAdEnd($event)"
        @ad-error="$emit('ad-error', $event)"
        @content-resume="$emit('content-resume', $event)"
        @content-ended="$emit('content-ended', $event)"
        ref="tvbPlayerRef"
        v-if="props.type === 'shaka'"
      />
    </component>

    <!-- 推荐视频区域 -->
    <TvbRecommendVideos
      :visible="showRecommendations"
      :featured-videos="props.featuredVideos"
      :is-mobile="isMobile"
      @replay="onReplay"
      @next="onCountdownNext"
      @navigate="onRecommendNavigate"
      class="recommend-videos-wrapper"
      :class="{ 'is-mobile': isMobile }"
    />
  </div>
</template>

<style lang="scss" scoped>
.tvb-player {
  position: relative;
  &.error {
    background-color: #000;
  }

  .tvb-player-play-layer {
    position: absolute;
    width: fit-content;
    height: fit-content;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);

    .tvb-player-play {
      position: absolute;
      left: calc((100% - 48px) / 2);
      top: calc((100% - 48px) / 2);
      z-index: 9;
    }

    z-index: 9;
  }

  .tvb-player-container {
    width: 100%;
    height: 100%;
    transition: opacity 500ms;
    background-color: #000;

    > * {
      width: 100%;
      height: 100%;
    }
  }

  .tvb-player-error {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    gap: 24px;

    .tvb-player-error-icon {
      font-size: 56px;
    }

    .tvb-player-error-text {
      color: rgba(255, 255, 255, 0.9);
      font-size: 18px;
      font-weight: 400;
      line-height: 26px;
      letter-spacing: 0.5px;
    }
  }

  :deep(.n-spin) {
    --n-size: 48px !important;
  }

  &.s,
  &.xs {
    :deep(.n-spin) {
      --n-size: 40px !important;
    }

    .tvb-player-play {
      left: calc((100% - 40px) / 2);
      top: calc((100% - 40px) / 2);
    }
  }

  // 推荐视频区域包装器
  .recommend-videos-wrapper {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: 50;

    &.is-mobile {
      position: absolute;
      inset: 0;
      z-index: 100;
    }
  }
}
</style>
